import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActivateUserPopup from "./components/ActivateUserPopup";
import UserSearchBar from "./components/UserSearchBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FronteraPage = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [activeUser, setActiveUser] = useState(null);

  const [weeklyNewUsers, setWeeklyNewUsers] = useState(0);
  const [monthlyNewUsers, setMonthlyNewUsers] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllUsers = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(`${BACKEND_URL}/user/admin/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (!res.ok) {
          setError(data.message || "Failed to fetch users.");
          return;
        }

        const rawUsers = data.users || [];

        // ✅ Deduplicate users by lowercase email
        const uniqueUsersMap = {};
        const deduplicated = rawUsers.filter((user) => {
          if (!user.email) return false;
          const email = user.email.toLowerCase();
          if (uniqueUsersMap[email]) return false;
          uniqueUsersMap[email] = true;
          return true;
        });

        setUsers(deduplicated);

        const now = new Date();
        const oneWeekAgo = new Date(now);
        oneWeekAgo.setDate(now.getDate() - 7);

        const oneMonthAgo = new Date(now);
        oneMonthAgo.setMonth(now.getMonth() - 1);

        // ✅ Weekly new users
        const weekly = deduplicated.filter((user) => {
          const createdAt = user.createdAt?._seconds
            ? new Date(user.createdAt._seconds * 1000)
            : new Date(user.createdAt);
          return createdAt >= oneWeekAgo;
        }).length;

        // ✅ Monthly new users
        const monthly = deduplicated.filter((user) => {
          const createdAt = user.createdAt?._seconds
            ? new Date(user.createdAt._seconds * 1000)
            : new Date(user.createdAt);
          return createdAt >= oneMonthAgo;
        }).length;

        setWeeklyNewUsers(weekly);
        setMonthlyNewUsers(monthly);
      } catch (err) {
        console.error("❌ Error fetching users:", err);
        setError("Something went wrong.");
      }
    };

    fetchAllUsers();
  }, []);


  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    let dateObj;
    if (timestamp._seconds) dateObj = new Date(timestamp._seconds * 1000);
    else if (timestamp.seconds) dateObj = new Date(timestamp.seconds * 1000);
    else if (typeof timestamp === "string" && !isNaN(Date.parse(timestamp))) dateObj = new Date(timestamp);
    else if (timestamp instanceof Date && !isNaN(timestamp.getTime())) dateObj = timestamp;
    else return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleClick = (email) => {
    if (!email) return;
    const username = email.split("@")[0];
    navigate(`/${username}`);
  };

  const filteredUsers = users.filter((user) => {
    const q = searchQuery.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(q) ||
      user.lastName?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      user.username?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen px-6 py-10 bg-white">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              🌐 Frontera Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Total Users:{" "}
              <span className="font-medium text-blue-600">{users.length}</span>
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-gray-500">
              <p>
                🆕 This Week:{" "}
                <span className="text-blue-600 font-medium">{weeklyNewUsers}</span>
              </p>
              <p className="flex items-center gap-2">
                📅 This Month:{" "}
                <span className="text-green-600 font-medium">{monthlyNewUsers}</span>
                <button
                  onClick={() =>
                    navigate("/fronteracloud/profile/frontera-dashboard/user-history")
                  }
                  className="px-3 py-1 rounded-md text-xs bg-gray-100 border border-gray-300 hover:bg-gray-200 text-gray-800 transition"
                >
                  View History →
                </button>
              </p>
            </div>
          </div>

          <UserSearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 border border-red-300 rounded px-4 py-2 mb-6">
          {error}
        </div>
      )}

      {filteredUsers.length === 0 && !error ? (
        <p className="text-sm text-gray-500">No matching users found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <div
              key={user.email}
              className="bg-gray-50 p-5 rounded-lg shadow hover:shadow-md transition"
              onClick={() => handleClick(user.email)}
            >
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={user.profileImage || "/person.png"}
                  alt={user.firstName}
                  className="w-16 h-16 rounded-full object-cover border"
                />
                <div>
                  <p className="font-semibold text-gray-800">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <p className="text-xs text-gray-400 break-all">🆔 {user.id || "N/A"}</p>
                  <p className="text-xs text-green-600">
                    {user.emailVerified ? "✅ Verified" : "❌ Unverified"}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-1">
                📍 {user.city || "Unknown"}, {user.country || ""}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                🌐 Username: <span className="font-medium">{user.username}</span>
              </p>
              <p className="text-sm text-gray-600 mb-1">
                🧠 Interests: <span className="italic">{user.interests || "None"}</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Joined: {formatDate(user.createdAt)}
              </p>

              <button
                onClick={(e) => {
                  setActiveUser(user);
                  e.stopPropagation();
                }}
                className="mt-4 px-3 py-1 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
              >
                ✨ Activate
              </button>
            </div>
          ))}
        </div>
      )}

      {activeUser && (
        <ActivateUserPopup
          user={activeUser}
          interests={activeUser.interests || ""}
          firstName={activeUser.firstName}
          onClose={() => setActiveUser(null)}
        />
      )}
    </div>
  );
};

export default FronteraPage;
